/**
 * 8 Ways to Think With Littlebird
 *
 * The guide document is unchanged; only its wrapper is. It used to carry
 * its own 49px strip with a back link and no footer, so a visitor who landed
 * here from an email had no wordmark, no account control and no way into the
 * rest of the site. GuideShell supplies all three, and GuideFrame lets the
 * document size itself so the footer sits after it rather than behind a
 * second scrollbar.
 */

import GuideShell from "@/components/GuideShell";
import GuideFrame from "@/components/GuideFrame";

const HTML_SRC = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>8 Ways to Think With Littlebird | Caren Glasser</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;1,9..144,400;1,9..144,600&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap" rel="stylesheet">

<style>
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --cream: oklch(0.98 0.008 85);
    --linen: oklch(0.93 0.01 85);
    --espresso: oklch(0.22 0.015 65);
    --terracotta: oklch(0.32 0.08 255);
    --border: oklch(0.88 0.012 85);
    --white: oklch(1 0.004 85);
    --terracotta-light: oklch(0.32 0.08 255 / 0.08);
    --terracotta-mid: oklch(0.32 0.08 255 / 0.22);
    --muted: oklch(0.52 0.015 65);
    --body: oklch(0.52 0.015 65);
  }

  body {
    background: var(--cream);
    color: var(--espresso);
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    min-height: 100vh;
    padding: 40px 20px 60px;
  }

  .container {
    max-width: 680px;
    margin: 0 auto;
  }

  /* HEADER */
  .header {
    text-align: center;
    margin-bottom: 48px;
    padding-bottom: 36px;
    border-bottom: 1px solid var(--border);
  }

  .header .eyebrow {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--terracotta);
    margin-bottom: 14px;
  }

  .header h1 {
    font-family: 'Fraunces', Georgia, serif;
    font-size: clamp(28px, 5vw, 38px);
    font-weight: 700;
    color: var(--espresso);
    line-height: 1.2;
    margin-bottom: 14px;
  }

  .header p {
    font-size: 16px;
    color: var(--muted);
    line-height: 1.6;
    max-width: 480px;
    margin: 0 auto 20px;
  }

  .progress-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-top: 20px;
  }

  .progress-bar {
    width: 200px;
    height: 4px;
    background: var(--border);
    border-radius: 2px;
    overflow: hidden;
  }

  .progress-fill {
    height: 100%;
    background: var(--terracotta);
    border-radius: 2px;
    transition: width 0.5s ease;
    width: 0%;
  }

  .progress-text {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    color: oklch(0.52 0.015 65);
  }

  /* STEPS */
  .step {
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 3px;
    margin-bottom: 16px;
    overflow: hidden;
    transition: all 0.3s ease;
  }

  .step.completed {
    border-color: var(--terracotta-mid);
    background: var(--terracotta-light);
  }

  .step-header {
    display: flex;
    align-items: flex-start;
    gap: 18px;
    padding: 24px 28px;
    cursor: pointer;
    user-select: none;
  }

  .step-check {
    width: 28px;
    height: 28px;
    border: 2px solid var(--border);
    border-radius: 50%;
    flex-shrink: 0;
    margin-top: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    background: var(--white);
  }

  .step.completed .step-check {
    background: var(--terracotta);
    border-color: var(--terracotta);
  }

  .step-check svg {
    opacity: 0;
    transform: scale(0.5);
    transition: all 0.25s ease;
  }

  .step.completed .step-check svg {
    opacity: 1;
    transform: scale(1);
  }

  .step-title-wrap { flex: 1; }

  .step-number {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--terracotta);
    margin-bottom: 4px;
  }

  .step.completed .step-number { color: oklch(0.26 0.075 255); }

  .step-title {
    font-family: 'Fraunces', Georgia, serif;
    font-size: 20px;
    font-weight: 600;
    color: var(--espresso);
    line-height: 1.3;
  }

  .step-toggle {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 20px;
    color: var(--border);
    flex-shrink: 0;
    line-height: 1;
    margin-top: 4px;
    transition: transform 0.3s ease, color 0.3s ease;
  }

  .step.open .step-toggle { transform: rotate(45deg); color: var(--terracotta); }

  /* STEP BODY */
  .step-body {
    display: none;
    padding: 0 28px 28px 74px;
  }

  .step.open .step-body { display: block; }

  .step-body p {
    font-size: 16px;
    line-height: 1.7;
    color: var(--body);
    margin-bottom: 14px;
  }

  .step-body ul {
    list-style: none;
    margin-bottom: 16px;
  }

  .step-body ul li {
    font-size: 16px;
    line-height: 1.6;
    color: var(--body);
    padding: 5px 0 5px 20px;
    position: relative;
  }

  .step-body ul li::before {
    content: "—";
    position: absolute;
    left: 0;
    color: var(--terracotta);
    font-weight: 300;
  }

  /* PRINCIPLE BOX */
  .principle-box {
    background: var(--linen);
    border-left: 3px solid var(--terracotta);
    border-radius: 0 3px 3px 0;
    padding: 14px 18px;
    margin: 12px 0 16px;
  }

  .principle-box .label {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--terracotta);
    margin-bottom: 6px;
  }

  .principle-box p {
    font-size: 14px;
    color: var(--espresso);
    line-height: 1.6;
    margin: 0;
    font-style: italic;
  }

  /* MARK DONE */
  .mark-done-btn {
    display: inline-block;
    margin-top: 8px;
    padding: 10px 22px;
    background: var(--terracotta);
    color: var(--white);
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.03em;
    border: none;
    border-radius: 2px;
    cursor: pointer;
    transition: background 0.2s ease;
  }

  .mark-done-btn:hover { background: oklch(0.26 0.075 255); }
  .step.completed .mark-done-btn { background: oklch(0.26 0.075 255); }

  /* TIP BOX */
  .tip-box {
    background: var(--linen);
    border: 1px solid var(--border);
    border-radius: 3px;
    padding: 24px 28px;
    margin-top: 8px;
    display: flex;
    gap: 16px;
    align-items: flex-start;
  }

  .tip-icon { font-size: 24px; flex-shrink: 0; margin-top: 1px; }

  .tip-box p {
    font-size: 16px;
    line-height: 1.7;
    color: var(--body);
    margin: 0;
  }

  /* COMPLETION */
  .completion {
    display: none;
    text-align: center;
    padding: 40px 28px;
    background: var(--white);
    border: 1px solid var(--terracotta-mid);
    border-radius: 3px;
    margin-top: 8px;
  }

  .completion.visible { display: block; }
  .completion .icon { font-size: 36px; margin-bottom: 16px; }

  .completion h2 {
    font-family: 'Fraunces', Georgia, serif;
    font-size: 24px;
    color: var(--espresso);
    margin-bottom: 10px;
  }

  .completion p {
    font-size: 16px;
    color: var(--muted);
    line-height: 1.6;
    max-width: 420px;
    margin: 0 auto 20px;
  }

  .completion a {
    display: inline-block;
    padding: 12px 28px;
    background: var(--terracotta);
    color: var(--white);
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 14px;
    font-weight: 600;
    border-radius: 2px;
    text-decoration: none;
    transition: background 0.2s;
  }

  .completion a:hover { background: oklch(0.26 0.075 255); }

  /* FOOTER */
  .footer {
    text-align: center;
    margin-top: 48px;
    padding-top: 28px;
    border-top: 1px solid var(--border);
  }

  .footer p {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    color: oklch(0.88 0.012 85);
    letter-spacing: 0.04em;
  }

  .footer a { color: var(--terracotta); text-decoration: none; }
</style>
</head>
<body>
<div class="container">

  <!-- HEADER -->
  <div class="header">
    <p class="eyebrow">Resource Center: Smart Stack</p>
    <h1>8 Ways to Think<br>With Littlebird</h1>
    <p>Because the best AI memory is the one that was already running before you thought to ask.</p>
    <div class="progress-wrap">
      <div class="progress-bar"><div class="progress-fill" id="progressFill"></div></div>
      <span class="progress-text" id="progressText">0 of 8 complete</span>
    </div>
  </div>

  <!-- WAY 1 -->
  <div class="step open" id="step1">
    <div class="step-header" onclick="toggleStep('step1')">
      <div class="step-check">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2.5 7L5.5 10L11.5 4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="step-title-wrap">
        <div class="step-number">Way 01</div>
        <div class="step-title">It Knows Your Work Before You Say a Word</div>
      </div>
      <div class="step-toggle">+</div>
    </div>
    <div class="step-body">
      <p>Most AI tools need you to explain yourself before they can help. Littlebird doesn't. It runs quietly in the background, reading your screen and listening to your meetings, building a private memory of your work without asking anything of you.</p>
      <p>When you finally open it, it already knows what's been happening. That's a fundamentally different relationship with an AI tool.</p>
      <div class="principle-box">
        <div class="label">The Shift</div>
        <p>Stop thinking of Littlebird as a chatbot you have to brief. Think of it as a thinking partner who's already been in the room.</p>
      </div>
      <button class="mark-done-btn" onclick="markDone('step1')">✓ Got it</button>
    </div>
  </div>

  <!-- WAY 2 -->
  <div class="step" id="step2">
    <div class="step-header" onclick="toggleStep('step2')">
      <div class="step-check">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2.5 7L5.5 10L11.5 4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="step-title-wrap">
        <div class="step-number">Way 02</div>
        <div class="step-title">Let It Observe Before You Expect Anything</div>
      </div>
      <div class="step-toggle">+</div>
    </div>
    <div class="step-body">
      <p>The instinct is to open a new tool and immediately put it to work. Resist that with Littlebird. Download it, let it run in the background through a full workday, and don't ask it anything yet.</p>
      <p>The context it builds on day one becomes the foundation everything else runs on. Patience in setup pays back in relevance.</p>
      <ul>
        <li>Download at littlebird.ai (free to start, no invite required)</li>
        <li>Let it run in the background while you work normally</li>
        <li>Connect your calendar and email for deeper context (optional but recommended)</li>
        <li>Don't try to use it heavily on day one. Give it a full workday first</li>
      </ul>
      <div class="principle-box">
        <div class="label">Smart Stack Principle</div>
        <p>Patience in setup pays back in relevance.</p>
      </div>
      <button class="mark-done-btn" onclick="markDone('step2')">✓ Got it</button>
    </div>
  </div>

  <!-- WAY 3 -->
  <div class="step" id="step3">
    <div class="step-header" onclick="toggleStep('step3')">
      <div class="step-check">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2.5 7L5.5 10L11.5 4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="step-title-wrap">
        <div class="step-number">Way 03</div>
        <div class="step-title">Walk Into Any Meeting Fully Prepared</div>
      </div>
      <div class="step-toggle">+</div>
    </div>
    <div class="step-body">
      <p>Before a meeting, ask Littlebird to brief you on who you're meeting, pulling from your email history, their public background, and your calendar notes. Ask for three talking points and one collaboration idea.</p>
      <p>Two minutes of review replaces twenty minutes of prep. You walk in knowing more than the other person expects.</p>
      <ul>
        <li>Open Littlebird before the meeting</li>
        <li>Ask it to research who you're meeting</li>
        <li>Request 3–5 talking points and one idea for collaboration</li>
        <li>Review for two minutes, then walk in</li>
      </ul>
      <div class="principle-box">
        <div class="label">Smart Stack Principle</div>
        <p>Preparation shouldn't cost you the time it saves.</p>
      </div>
      <button class="mark-done-btn" onclick="markDone('step3')">✓ Got it</button>
    </div>
  </div>

  <!-- WAY 4 -->
  <div class="step" id="step4">
    <div class="step-header" onclick="toggleStep('step4')">
      <div class="step-check">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2.5 7L5.5 10L11.5 4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="step-title-wrap">
        <div class="step-number">Way 04</div>
        <div class="step-title">Close the Loop After Every Meeting, Automatically</div>
      </div>
      <div class="step-toggle">+</div>
    </div>
    <div class="step-body">
      <p>Let Littlebird run during the meeting. Afterward, ask it to draft your follow-up email and list your commitments. The whole loop (prep, notes, follow-up) closes in one conversation thread in under five minutes.</p>
      <p>The follow-up you always mean to send stops being the task you always forget.</p>
      <ul>
        <li>Let Littlebird run during your meeting; it transcribes automatically</li>
        <li>After the meeting, ask it to draft your follow-up email</li>
        <li>Ask it to list your commitments from the conversation</li>
        <li>Review and send. The whole loop closes in under five minutes</li>
      </ul>
      <div class="principle-box">
        <div class="label">Smart Stack Principle</div>
        <p>The loop that closes itself removes the task you always forget.</p>
      </div>
      <button class="mark-done-btn" onclick="markDone('step4')">✓ Got it</button>
    </div>
  </div>

  <!-- WAY 5 -->
  <div class="step" id="step5">
    <div class="step-header" onclick="toggleStep('step5')">
      <div class="step-check">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2.5 7L5.5 10L11.5 4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="step-title-wrap">
        <div class="step-number">Way 05</div>
        <div class="step-title">Use Hummingbird When Switching Windows Breaks Your Flow</div>
      </div>
      <div class="step-toggle">+</div>
    </div>
    <div class="step-body">
      <p>Press the Hummingbird shortcut and a floating window opens over whatever you're working on. Ask a question, get a summary, request a draft, without leaving your current app.</p>
      <p>Littlebird answers using context it already has. Close it and keep working. No tab switching. No lost momentum.</p>
      <ul>
        <li>Press the Hummingbird keyboard shortcut from any app</li>
        <li>A floating window appears over your current work</li>
        <li>Ask anything. Littlebird uses context it already has to answer</li>
        <li>Close it and keep going. No tab switching, no lost momentum</li>
      </ul>
      <div class="principle-box">
        <div class="label">Smart Stack Principle</div>
        <p>The best tool is the one that doesn't interrupt the work.</p>
      </div>
      <button class="mark-done-btn" onclick="markDone('step5')">✓ Got it</button>
    </div>
  </div>

  <!-- WAY 6 -->
  <div class="step" id="step6">
    <div class="step-header" onclick="toggleStep('step6')">
      <div class="step-check">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2.5 7L5.5 10L11.5 4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="step-title-wrap">
        <div class="step-number">Way 06</div>
        <div class="step-title">Ask for Your Weekly Productivity Mirror</div>
      </div>
      <div class="step-toggle">+</div>
    </div>
    <div class="step-body">
      <p>At the end of the week, ask Littlebird to summarize what you worked on, which areas got the most time, and one thing to improve next week.</p>
      <p>Read it without judgment. You can't improve what you can't see clearly, and Littlebird has been watching all week so you didn't have to track anything yourself.</p>
      <ul>
        <li>Ask: "Summarize what I worked on this week"</li>
        <li>Ask: "Which areas got the most time, and which got the least?"</li>
        <li>Ask: "What's one thing I should streamline or improve next week?"</li>
        <li>Read it without judgment, then decide what to change</li>
      </ul>
      <div class="principle-box">
        <div class="label">Smart Stack Principle</div>
        <p>You can't improve what you can't see clearly.</p>
      </div>
      <button class="mark-done-btn" onclick="markDone('step6')">✓ Got it</button>
    </div>
  </div>

  <!-- WAY 7 -->
  <div class="step" id="step7">
    <div class="step-header" onclick="toggleStep('step7')">
      <div class="step-check">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2.5 7L5.5 10L11.5 4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="step-title-wrap">
        <div class="step-number">Way 07</div>
        <div class="step-title">Stay in Control of What It Sees</div>
      </div>
      <div class="step-toggle">+</div>
    </div>
    <div class="step-body">
      <p>Always-on doesn't mean no boundaries. You choose which apps Littlebird observes and which it ignores. You can pause context collection with one click. You can delete recent data (the last hour, the last day, or all of it) whenever you choose.</p>
      <p>Littlebird never sees minimized apps, private browser windows, or password fields. Trust requires the ability to say stop.</p>
      <ul>
        <li>Go to settings and choose which apps Littlebird observes</li>
        <li>Pause context collection anytime with one click. It stops immediately</li>
        <li>Delete recent data (last hour, last day, or all of it) whenever you want</li>
        <li>Private browser windows and password fields are never visible to it</li>
      </ul>
      <div class="principle-box">
        <div class="label">Smart Stack Principle</div>
        <p>Trust requires transparency and the ability to say stop.</p>
      </div>
      <button class="mark-done-btn" onclick="markDone('step7')">✓ Got it</button>
    </div>
  </div>

  <!-- WAY 8 -->
  <div class="step" id="step8">
    <div class="step-header" onclick="toggleStep('step8')">
      <div class="step-check">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2.5 7L5.5 10L11.5 4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="step-title-wrap">
        <div class="step-number">Way 08</div>
        <div class="step-title">Stop Managing It, Start Reaching for It</div>
      </div>
      <div class="step-toggle">+</div>
    </div>
    <div class="step-body">
      <p>The goal isn't to check Littlebird every day. It's to reach for it only when you need it, and find that it's already been building context while you were focused elsewhere.</p>
      <p>Query it like memory, not a filing system. Pair it with your other tools: let Littlebird hold context while they handle execution. The tools that ask the least often give the most back.</p>
      <ul>
        <li>Stop opening it out of habit. Reach for it when you actually need it</li>
        <li>Trust that it's been building context even when you haven't asked anything</li>
        <li>Use it as a reference, not a dashboard</li>
        <li>Pair it with your other tools: let this layer handle context while they handle execution</li>
      </ul>
      <div class="principle-box">
        <div class="label">Smart Stack Principle</div>
        <p>The tools that ask the least often give the most back.</p>
      </div>
      <button class="mark-done-btn" onclick="markDone('step8')">✓ Got it</button>
    </div>
  </div>

  <!-- TIP -->
  <div class="tip-box">
    <div class="tip-icon">🐦</div>
    <p><strong>The short version:</strong> Littlebird works best when you stop thinking about it. It's not the tool you open to start your day. It's the one that was already running before you got there. Let it remember. Reach for it when it matters.</p>
  </div>

  <!-- COMPLETION -->
  <div class="completion" id="completion">
    <div class="icon">🎉</div>
    <h2>You've got the full picture.</h2>
    <p>Now stop thinking about Littlebird and let it do its job. It's already running. Reach for it when you need it.</p>
    <a href="https://aitools.yoursmartstack.com/" target="_blank">Explore the Resource Center →</a>
  </div>

  <!-- FOOTER -->

</div>

<script>
  const steps = ['step1','step2','step3','step4','step5','step6','step7','step8'];

  function toggleStep(id) {
    const el = document.getElementById(id);
    const isOpen = el.classList.contains('open');
    steps.forEach(s => document.getElementById(s).classList.remove('open'));
    if (!isOpen) el.classList.add('open');
  }

  function markDone(id) {
    const el = document.getElementById(id);
    el.classList.add('completed');
    el.classList.remove('open');
    updateProgress();
  }

  function updateProgress() {
    const done = steps.filter(s => document.getElementById(s).classList.contains('completed')).length;
    const pct = (done / steps.length) * 100;
    document.getElementById('progressFill').style.width = pct + '%';
    document.getElementById('progressText').textContent = done + ' of ' + steps.length + ' complete';
    if (done === steps.length) {
      document.getElementById('completion').classList.add('visible');
    }
  }

  /* No toggleStep('step1') call here, deliberately. Step 1 already carries the
     open class in the markup, so calling toggle CLOSES it — every one of these
     guides loaded with all its cards shut and nothing to show that a card opens
     at all. The markup is the intent. Fixed 4 Sep 2026. */
</script>
</body>
</html>
`;

export default function LittlebirdEightWays() {
  return (
    <GuideShell
      context={"Littlebird guide"}
      backHref={"/resources/littlebird"}
      backLabel={"Back to Littlebird"}
    >
      <GuideFrame html={HTML_SRC} title={"8 Ways to Think With Littlebird"} />
    </GuideShell>
  );
}
